<template>
	<b-modal
		id="new-thread-modal"
		:visible="visible"
		size="xl"
		centered
		hide-header
		hide-footer
		no-close-on-backdrop
		modal-class="custom-modal forum-thread-modal"
		dialog-class="animate-modal-dialog"
		content-class="forum-thread-modal-content"
		body-class="modal-body"
		@change="onVisibleChange"
		@hidden="reset"
	>
		<div class="body-container">
			<div class="right-close-panel">
				<button type="button" class="close-button" role="button" aria-label="Close" @click="close">
					<img src="@/assets/images/cancel-icon.svg" alt="Close" />
				</button>
			</div>

			<div class="main-content">
				<div class="info-container expand">
					<h1 class="modal-title">Start a Discussion</h1>

					<div class="step-content">
						<div class="form-group my-3">
							<label class="main-label" for="forum-thread-title-input">
								Title <span class="required-star">*</span>
							</label>
							<div class="textfield-wrapper">
								<input
									id="forum-thread-title-input"
									v-model="title"
									type="text"
									class="form-input"
									placeholder="What's your question or topic?"
									maxlength="200"
								/>
								<div class="text-right text-sm text-gray-500 mt-1">
									{{ title.length }}/200
								</div>
							</div>
						</div>

						<div class="form-group my-3">
							<label class="main-label">
								Body <span class="required-star">*</span>
							</label>
							<div class="editor-wrapper">
								<MarkdownEditor
									v-if="visible"
									ref="editor"
									:key="editorKey"
									:autofocus="false"
									:compact="true"
									:show-actions="false"
									:allow-mentions="mentionsEnabled"
									:search-users="debouncedMentionSearch"
									:upload-entity="entityType.Thread"
									:allow-attachments="threadAttachmentPolicy.isAllowed"
									:allowed-attachment-types="threadAttachmentPolicy.allowedTypes"
									placeholder="Share details, questions, code snippets — drop files to attach…"
									send-label="Post"
									@update="onEditorUpdate"
								/>
							</div>
						</div>
					</div>
				</div>

				<div class="step-btn-container">
					<div class="step-navigation">
						<button type="button" class="step-btn secondary" @click="close">
							Cancel
						</button>
						<button
							type="button"
							class="step-btn"
							:disabled="!canSubmit || isSubmitting"
							@click="onSubmit"
						>
							{{ isSubmitting ? "Posting…" : "Post" }}
						</button>
					</div>
				</div>
			</div>
		</div>
	</b-modal>
</template>

<script>
import { mapState } from "vuex";
import MarkdownEditor from "./Editor/MarkdownEditor.vue";
import { EntityType } from "../../constants/entity";
import { MENTIONS_ENABLED } from "../../constants/forum";
import { resolveAttachmentPolicy } from "../../utils/attachment-utility";
import { createMentionSearch } from "../../utils/mention-search";

export default {
	name: "NewThreadModal",
	components: { MarkdownEditor },
	props: {
		visible: { type: Boolean, default: false },
		productVariantId: { type: [Number, String], default: null },
		isSubmitting: { type: Boolean, default: false },
	},
	data() {
		return {
			title: "",
			editorPayload: null,
			editorKey: Date.now(),
			entityType: EntityType,
			mentionsEnabled: MENTIONS_ENABLED,
			debouncedMentionSearch: createMentionSearch({
				productVariantId: this.productVariantId,
			}),
		};
	},
	computed: {
		...mapState("appState", ["systemParameters"]),
		threadAttachmentPolicy() {
			return resolveAttachmentPolicy(this.systemParameters, EntityType.Thread);
		},
		// canSubmit watches the editor's running JSON output via @update.
		// Empty editor = `editorPayload.text` is just whitespace.
		canSubmit() {
			if (!this.title.trim()) return false;
			if (!this.editorPayload) return false;
			const text = (this.editorPayload.text || "").trim();
			const md   = (this.editorPayload.markdown || "").trim();
			return text.length > 0 || md.length > 0;
		},
	},
	methods: {
		close() {
			this.$emit("close");
		},
		onVisibleChange(nextVisible) {
			if (!nextVisible) this.$emit("close");
		},
		reset() {
			this.title = "";
			this.editorPayload = null;
			this.editorKey = Date.now();
		},
		onEditorUpdate(payload) {
			this.editorPayload = payload;
		},
		onSubmit() {
			if (!this.canSubmit || this.isSubmitting) return;

			// Pull the latest serialized payload directly from the editor in
			// case the user clicked Post before @update fired (race-safe).
			const payload = this.$refs.editor && this.$refs.editor.serialize
				? this.$refs.editor.serialize()
				: this.editorPayload;

			const attachmentIds = ((payload && payload.attachments) || [])
				.filter((attachment) => !attachment.uploading)
				.map((attachment) => attachment.id)
				.filter((attachmentId) => attachmentId != null);

			this.$emit("submit", {
				title: this.title.trim(),
				body: (payload && payload.markdown) || "",
				attachmentIds,
			});
		},
	},
	watch: {
		visible(isVisible) {
			if (isVisible) this.editorKey = Date.now();
		},
		productVariantId(next) {
			this.debouncedMentionSearch = createMentionSearch({
				productVariantId: next,
			});
		},
	},
};
</script>

<style>
/* BV mounts modals on <body>, so scoped styles can't reach the dialog.
   Every selector here is gated on .forum-thread-modal so it can't leak
   into other modals */
.forum-thread-modal .modal-body.modal-body {
	display: flex;
	flex-direction: row;
	padding: 0 !important;
	overflow: hidden;
}

.forum-thread-modal .body-container {
	padding: 0;
	display: flex;
	flex-direction: column;
	width: 100%;
	position: relative;
}

.forum-thread-modal .right-close-panel {
	position: absolute;
	top: 1.75rem;
	right: 1.75rem;
	display: flex;
	z-index: 2;
}
.forum-thread-modal .right-close-panel .close-button {
	position: initial;
	background: transparent;
	border: none;
	padding: 4px;
	cursor: pointer;
}
.forum-thread-modal .right-close-panel .close-button img {
	width: 22px;
	height: 22px;
	display: block;
}

.forum-thread-modal .main-content {
	flex: 1;
	display: flex;
	flex-direction: column;
	height: 100%;
	width: 100%;
}

.forum-thread-modal .info-container.expand {
	flex: 1;
	display: flex;
	flex-direction: column;
	min-height: 0;
}
.forum-thread-modal .info-container.expand .step-content {
	height: 60vh;
	overflow: auto;
	padding: 0 2rem;
}

.forum-thread-modal .modal-title {
	color: #000;
	font-family: "Circular Std", "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
	font-size: 1.75rem;
	font-style: normal;
	font-weight: 450;
	line-height: 1.2;
	margin: 2.5rem 2rem 1.25rem;
	letter-spacing: -0.01em;
}

.forum-thread-modal .main-label {
	font-weight: 500;
	font-size: 1rem;
	line-height: 1.37;
	text-transform: uppercase;
	color: #727272;
	margin-bottom: 0.75rem;
	display: block;
}
.forum-thread-modal .required-star { color: #d3131a; }

.forum-thread-modal .form-input {
	border: 0;
	border-bottom: 1.5px solid #b2b2b2;
	font-size: 1rem;
	outline: none;
	padding: 0.875rem 0;
	width: 100%;
	background: transparent;
	font-family: inherit;
	color: #0f0f0f;
}
.forum-thread-modal .form-input:focus {
	border-color: var(--primary-color, #ffd83f) !important;
}

.forum-thread-modal .editor-wrapper {
	border: 1px solid #ececec;
	border-radius: 8px;
	background: #fafafa;
	padding: 4px;
	transition: border-color 0.15s ease, background-color 0.15s ease;
}
.forum-thread-modal .editor-wrapper:focus-within {
	border-color: var(--secondary-color, #03324d);
	background: #ffffff;
	box-shadow: 0 0 0 3px rgba(3, 50, 77, 0.08);
}

/* Bottom action bar — mirrors .step-btn-container / .step-navigation
   from the certificate modal so the button stack reads identically. */
.forum-thread-modal .step-btn-container {
	border-top: 1px solid #f0f0f0;
}
.forum-thread-modal .step-navigation {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 1rem;
	width: 100%;
	padding: 1.25rem 2rem;
}

.forum-thread-modal .step-btn {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 0.6rem 1.5rem;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	border: none;
	cursor: pointer;
	background: var(--primary-color, #ffd83f);
	color: #ffffff;
	font-family: inherit;
	transition: opacity 0.15s ease, background-color 0.15s ease, color 0.15s ease, transform 0.1s ease;
}
.forum-thread-modal .step-btn:hover:not(:disabled) { opacity: 0.88; }
.forum-thread-modal .step-btn:active:not(:disabled) { transform: scale(0.98); }
.forum-thread-modal .step-btn:disabled {
	background: #f2f2f2;
	color: #909090;
	cursor: not-allowed;
}

/* Cancel button — neutral grey shell. Doubled selector
   (`.forum-thread-modal.forum-thread-modal`) raises specificity above
   the base `.forum-thread-modal .step-btn` primary-yellow rule, and the
   explicit overrides for background-color / opacity / color on :hover
   stop the inherited primary-color hover from leaking through. */
.forum-thread-modal.forum-thread-modal .step-btn.secondary {
	background-color: #ffffff;
	border: 1px solid #e5e7eb;
	color: #374151;
	opacity: 1;
}
.forum-thread-modal.forum-thread-modal .step-btn.secondary:hover:not(:disabled) {
	background-color: #f3f4f6;
	border-color: #d1d5db;
	color: #111827;
	opacity: 1;
}
.forum-thread-modal.forum-thread-modal .step-btn.secondary:active:not(:disabled) {
	background-color: #e5e7eb;
}
.forum-thread-modal.forum-thread-modal .step-btn.secondary:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

/* Standard Bootstrap modal tier (1050) and backdrop (1040) — matches
   what every other BV modal in the app uses. We previously had to
   inflate this to 1000050 to fight an over-aggressive header z-index;
   now that Layout/Header.vue sits at the conventional 1030 ("fixed
   nav" tier), the default modal tier wins naturally. */
.forum-thread-modal.modal { z-index: 1050; }
.forum-thread-modal.modal ~ .modal-backdrop { z-index: 1040; }

/* Centre vertically — BV's `centered` prop uses flex; this safeguards
   layouts where align-items isn't being inherited reliably. */
.forum-thread-modal.modal .modal-dialog {
	min-height: calc(100% - 3.5rem);
	display: flex;
	align-items: center;
}

.forum-thread-modal-content {
	border: none;
	border-radius: 12px;
	box-shadow: 0 24px 64px rgba(0, 0, 0, 0.18),
	            0 2px 8px rgba(0, 0, 0, 0.08);
	overflow: hidden;
}

/* ---------- Tablet: 768px and below ---------- */
@media (max-width: 768px) {
	.forum-thread-modal .modal-title { margin: 2rem 1.25rem 1rem; font-size: 1.5rem; }
	.forum-thread-modal .info-container.expand .step-content { padding: 0 1.25rem; height: 65vh; }
	.forum-thread-modal .step-navigation { padding: 1rem 1.25rem; }
	.forum-thread-modal .right-close-panel { top: 1.25rem; right: 1.25rem; }
}

/* ---------- Phone: 600px and below ----------
   Bootstrap's .modal-dialog is centered with margins; on phones the xl
   modal would otherwise leave wasted edges. Push it toward fullscreen
   and let the editor + form breathe vertically. */
@media (max-width: 600px) {
	.forum-thread-modal.modal .modal-dialog {
		margin: 0;
		min-height: 100vh;
		max-width: 100vw;
		width: 100vw;
		align-items: stretch;
	}
	.forum-thread-modal-content {
		border-radius: 0;
		min-height: 100vh;
		display: flex;
		flex-direction: column;
	}
	.forum-thread-modal .modal-body.modal-body { flex: 1; }
	.forum-thread-modal .body-container { min-height: 100vh; }
	.forum-thread-modal .modal-title {
		margin: 1.5rem 1rem 0.75rem;
		font-size: 1.25rem;
	}
	.forum-thread-modal .info-container.expand .step-content {
		padding: 0 1rem;
		/* Fill remaining vertical space; the bottom action bar stays sticky
		   below this scroll region. iOS Safari uses 100dvh (dynamic vh) so
		   the bottom bar isn't covered by the URL chrome. */
		height: auto;
		flex: 1;
		min-height: 0;
		max-height: calc(100vh - 200px);
		max-height: calc(100dvh - 200px);
	}
	.forum-thread-modal .step-btn-container {
		position: sticky;
		bottom: 0;
		background: #ffffff;
	}
	.forum-thread-modal .step-navigation {
		padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom, 0));
		gap: 0.6rem;
	}
	.forum-thread-modal .step-btn {
		flex: 1;
		justify-content: center;
		padding: 0.7rem 1rem;
		font-size: 14px;
	}
	.forum-thread-modal .right-close-panel { top: 0.85rem; right: 0.85rem; }
	.forum-thread-modal .right-close-panel .close-button img { width: 20px; height: 20px; }
	.forum-thread-modal .form-input { font-size: 16px; }
	/* iOS auto-zooms inputs <16px on focus — keeping the body label
	   readable too, but the input is the critical one. */
	.forum-thread-modal .main-label { font-size: 0.85rem; }
}
</style>
